(function () {
  'use strict';

  /** @type {Window & Record<string, any>} */
  const runtimeWindow = window;
  const INIT_FLAG = '__aoneGoldTickerScriptInitialized';
  const CONTROLLER_KEY = '__aoneGoldTickerController';
  const CONTAINER_SELECTOR = '[data-gold-ticker-container]';
  const TRACK_SELECTOR = '[data-gold-ticker-track]';
  const GROUP_SELECTOR = '[data-gold-ticker-group]';

  /** @type {typeof window.AoneGoldPricing | undefined} */
  const goldPricing = runtimeWindow.AoneGoldPricing;

  const ORDERED_KEYS = Object.freeze(['spot', '10k', '14k', '18k', '22k', '24k']);
  const TROY_OUNCE_IN_GRAMS = 31.1034768;

  if (runtimeWindow[INIT_FLAG]) return;
  runtimeWindow[INIT_FLAG] = true;

  class GoldTickerController {
    constructor() {
      /** @type {number | null} */
      this.intervalId = null;
      /** @type {HTMLElement | null} */
      this.container = null;
      /** @type {HTMLElement | null} */
      this.track = null;
      /** @type {HTMLElement | null} */
      this.group = null;
      /** @type {AbortController | null} */
      this.abortController = null;
      /** @type {boolean} */
      this.fetchInFlight = false;
      /** @type {{ enabled: boolean; livePriceEnabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number; animationEnabled: boolean; speed: string; currencySymbol: string; spotLabel: string; visibleKeys: string[] } | null} */
      this.config = null;
      /** @type {string} */
      this.configSignature = '';
      /** @type {number | null} */
      this.lastSpotPrice = null;
      /** @type {string | null} */
      this.lastUpdatedText = null;
      /** @type {string} */
      this.lastRenderedSignature = '';
    }

    /** @param {HTMLElement} container */
    mount(container) {
      if (!(container instanceof HTMLElement)) return;

      const track = container.querySelector(TRACK_SELECTOR);
      const group = container.querySelector(GROUP_SELECTOR);
      if (!(track instanceof HTMLElement) || !(group instanceof HTMLElement)) return;

      const config = this.readConfig(container);
      const configSignature = this.createConfigSignature(config);

      const sameTarget = this.container === container && this.configSignature === configSignature;
      if (sameTarget) return;

      this.unmount(false);

      this.container = container;
      this.track = track;
      this.group = group;
      this.config = config;
      this.configSignature = configSignature;

      this.applySpeedClass(config.speed);
      this.applyAnimationMode(config.animationEnabled);

      if (!config.enabled) {
        this.setContainerVisible(true);
        return;
      }

      if (this.lastSpotPrice !== null) {
        this.renderFromSpot(this.lastSpotPrice, this.lastUpdatedText);
      } else if (config.livePriceEnabled) {
        this.setContainerVisible(false);
      } else {
        this.setContainerVisible(true);
      }

      if (!config.livePriceEnabled) return;

      this.refresh();
      if (config.autoRefresh) {
        this.intervalId = window.setInterval(() => {
          this.refresh();
        }, config.intervalMs);
      }
    }

    /**
     * @param {boolean} clearRenderState
     */
    unmount(clearRenderState = true) {
      this.clearInterval();
      this.abortController?.abort();
      this.abortController = null;
      this.fetchInFlight = false;

      if (clearRenderState) {
        this.lastRenderedSignature = '';
      }

      this.container = null;
      this.track = null;
      this.group = null;
      this.config = null;
      this.configSignature = '';
    }

    clearInterval() {
      if (this.intervalId === null) return;
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    /** @param {HTMLElement} container */
    readConfig(container) {
      /** @param {string | undefined} value */
      const parseBoolean = (value) => value === 'true';
      const rawInterval = Number.parseInt(container.dataset.goldPriceRefreshInterval || '', 10);
      const intervalSeconds = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 60;
      const speed = (container.dataset.goldTickerSpeed || 'slow').toLowerCase();
      // Always use London Fix label (do not rely on theme setting, which may still say "Gold Spot").
      const spotLabel = '2nd London Fix PM Gold';
      const currencySymbol = (container.dataset.goldTickerCurrencySymbol || '$').trim() || '$';

      /** @type {Record<string, boolean>} */
      const visibilityMap = {
        spot: parseBoolean(container.dataset.goldTickerShowSpot),
        '10k': parseBoolean(container.dataset.goldTickerShowTenK),
        '14k': parseBoolean(container.dataset.goldTickerShowFourteenK),
        '18k': parseBoolean(container.dataset.goldTickerShowEighteenK),
        '22k': parseBoolean(container.dataset.goldTickerShowTwentyTwoK),
        '24k': parseBoolean(container.dataset.goldTickerShowTwentyFourK),
      };

      const visibleKeys = ORDERED_KEYS.filter((key) => visibilityMap[key]);

      return {
        enabled: parseBoolean(container.dataset.goldTickerEnabled),
        livePriceEnabled: parseBoolean(container.dataset.goldPriceEnabled),
        baseUrl: (container.dataset.goldPriceApiBaseUrl || '').trim(),
        autoRefresh: parseBoolean(container.dataset.goldPriceAutoRefresh),
        intervalMs: intervalSeconds * 1000,
        animationEnabled: parseBoolean(container.dataset.goldTickerAnimation),
        speed: ['slow', 'medium', 'fast'].includes(speed) ? speed : 'slow',
        currencySymbol,
        spotLabel,
        visibleKeys: visibleKeys.length > 0 ? visibleKeys : ['spot', '24k'],
      };
    }

    /**
     * @param {{ enabled: boolean; livePriceEnabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number; animationEnabled: boolean; speed: string; currencySymbol: string; spotLabel: string; visibleKeys: string[] }} config
     */
    createConfigSignature(config) {
      return [
        config.enabled,
        config.livePriceEnabled,
        config.baseUrl,
        config.autoRefresh,
        config.intervalMs,
        config.animationEnabled,
        config.speed,
        config.currencySymbol,
        config.spotLabel,
        config.visibleKeys.join(','),
      ].join('|');
    }

    /** @param {string} speed */
    applySpeedClass(speed) {
      if (!this.container) return;

      this.container.classList.remove(
        'aone-gold-ticker--speed-slow',
        'aone-gold-ticker--speed-medium',
        'aone-gold-ticker--speed-fast'
      );
      this.container.classList.add(`aone-gold-ticker--speed-${speed}`);
    }

    /** @param {boolean} animationEnabled */
    applyAnimationMode(animationEnabled) {
      if (!this.container || !this.track || !this.group) return;

      this.container.classList.toggle('aone-gold-ticker--animated', animationEnabled);
    }

    /** @param {boolean} visible */
    setContainerVisible(visible) {
      if (!this.container) return;
      this.container.hidden = !visible;
      this.container.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    /** @param {string} baseUrl */
    buildEndpoint(baseUrl) {
      return `${baseUrl.replace(/\/+$/, '')}/api/gold-price/latest`;
    }

    /** @param {string} url */
    buildRequestHeaders(url) {
      /** @type {Record<string, string>} */
      const headers = { accept: 'application/json' };
      try {
        const host = new URL(url, window.location.origin).hostname.toLowerCase();
        if (host.includes('ngrok')) {
          headers['ngrok-skip-browser-warning'] = 'true';
        }
      } catch {
        /* ignore invalid URL */
      }
      return headers;
    }

    async refresh() {
      if (!this.config || !this.container || this.fetchInFlight) return;
      if (!this.config.livePriceEnabled || !this.config.baseUrl) {
        this.renderUnavailable();
        return;
      }

      this.fetchInFlight = true;
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const endpoint = this.buildEndpoint(this.config.baseUrl);
        const response = await fetch(endpoint, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: this.buildRequestHeaders(endpoint),
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Gold ticker request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const spotPrice = this.extractSpotPrice(payload);
        if (spotPrice === null) {
          throw new Error('Gold ticker response missing a numeric spot price');
        }

        if (goldPricing && typeof goldPricing.updateFromApiPayload === 'function') {
          goldPricing.updateFromApiPayload(payload, {
            currency: goldPricing.getActiveCurrencyCode(),
          });
        }

        const lastUpdatedText = this.extractLastUpdated(payload);
        const priceChanged = this.lastSpotPrice !== spotPrice;
        const updatedChanged = this.lastUpdatedText !== lastUpdatedText;

        if (priceChanged || updatedChanged) {
          this.lastSpotPrice = spotPrice;
          this.lastUpdatedText = lastUpdatedText;
          this.renderFromSpot(spotPrice, lastUpdatedText);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          if (this.lastSpotPrice === null) {
            this.renderUnavailable();
          }
          console.error('[gold-ticker] Failed to refresh ticker', error);
        }
      } finally {
        this.fetchInFlight = false;
      }
    }

    /** @param {any} payload */
    extractSpotPrice(payload) {
      const candidates = [
        payload?.data?.latest?.liveGoldPrice,
        payload?.data?.latest?.goldPM,
        payload?.data?.latest?.goldAM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.liveGoldPrice,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldPM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldAM,
        payload?.goldPrice,
        payload?.gold_price,
        payload?.price,
        payload?.latestPrice,
        payload?.latest_price,
        payload?.data?.price,
        payload?.data?.goldPrice,
        payload?.data?.gold_price,
      ];

      for (const candidate of candidates) {
        const numberValue = this.toNumber(candidate);
        if (numberValue !== null) return numberValue;
      }

      return null;
    }

    /** @param {any} payload */
    extractLastUpdated(payload) {
      const latest = payload?.data?.latest ?? payload?.latest ?? null;
      const historyFirst =
        payload?.data?.last20SuccessfulUpdates?.[0] ?? payload?.last20SuccessfulUpdates?.[0] ?? null;

      // Prefer London Fix priceDate (trading day) + time from fetch/update timestamp.
      const priceDateCandidates = [
        latest?.priceDate,
        latest?.price_date,
        historyFirst?.priceDate,
        historyFirst?.price_date,
        payload?.data?.priceDate,
        payload?.data?.price_date,
        payload?.priceDate,
        payload?.price_date,
      ];

      const timeCandidates = [
        latest?.updatedAt,
        latest?.updated_at,
        latest?.fetchedAt,
        latest?.fetched_at,
        latest?.createdAt,
        latest?.created_at,
        historyFirst?.updatedAt,
        historyFirst?.updated_at,
        historyFirst?.fetchedAt,
        historyFirst?.fetched_at,
        historyFirst?.createdAt,
        historyFirst?.created_at,
        payload?.data?.updatedAt,
        payload?.updatedAt,
      ];

      const timeSource = this.pickFirstValidDate(timeCandidates);

      for (const candidate of priceDateCandidates) {
        const formatted = this.formatPriceDate(candidate, timeSource);
        if (formatted) return formatted;
      }

      const stringCandidates = [
        latest?.lastUpdated,
        latest?.lastUpdatedLabel,
        latest?.last_updated,
        latest?.lastUpdatedDisplay,
        latest?.last_updated_display,
        latest?.updatedLabel,
        payload?.data?.lastUpdated,
        payload?.data?.lastUpdatedLabel,
        payload?.data?.last_updated,
        payload?.lastUpdated,
        payload?.lastUpdatedLabel,
        payload?.last_updated,
        historyFirst?.lastUpdated,
        historyFirst?.lastUpdatedLabel,
        historyFirst?.last_updated,
      ];

      for (const candidate of stringCandidates) {
        if (typeof candidate !== 'string') continue;
        const trimmed = candidate.trim();
        if (!trimmed) continue;
        if (/^last\s*updated\b/i.test(trimmed)) return trimmed;
        return `Last Updated: ${trimmed}`;
      }

      for (const candidate of timeCandidates) {
        const formatted = this.formatLastUpdated(candidate);
        if (formatted) return formatted;
      }

      return null;
    }

    /**
     * @param {unknown[]} candidates
     * @returns {Date | null}
     */
    pickFirstValidDate(candidates) {
      for (const candidate of candidates) {
        if (candidate == null || candidate === '') continue;
        const date = candidate instanceof Date ? candidate : new Date(String(candidate));
        if (!Number.isNaN(date.getTime())) return date;
      }
      return null;
    }

    /**
     * Formats London Fix trading day (priceDate) and optional fetch time.
     * Example: priceDate "03 Aug 2026" + updatedAt → "Last Updated: August 3, 2026, 10:35 AM CST"
     * @param {unknown} value
     * @param {Date | null} [timeSource]
     */
    formatPriceDate(value, timeSource = null) {
      if (value == null || value === '') return null;

      if (typeof value === 'string' && /^last\s*updated\b/i.test(value.trim())) {
        return value.trim();
      }

      const date = this.parsePriceDate(value);
      if (!date) return null;

      const datePart = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(date);

      const timePart = this.formatChicagoTime(timeSource);
      if (timePart) {
        return `Last Updated: ${datePart}, ${timePart}`;
      }

      return `Last Updated: ${datePart}`;
    }

    /**
     * Formats a timestamp's clock time in America/Chicago.
     * @param {Date | null | undefined} value
     * @returns {string | null}
     */
    formatChicagoTime(value) {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;

      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).formatToParts(value);

      /** @type {Record<string, string>} */
      const byType = {};
      for (const part of parts) {
        if (part.type !== 'literal') byType[part.type] = part.value;
      }

      if (!byType.hour || !byType.minute || !byType.dayPeriod) return null;
      return `${byType.hour}:${byType.minute} ${byType.dayPeriod} CST`;
    }

    /**
     * Parses API priceDate values without shifting the calendar day across timezones.
     * @param {unknown} value
     * @returns {Date | null}
     */
    parsePriceDate(value) {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      if (typeof value !== 'string' && typeof value !== 'number') return null;

      const raw = String(value).trim();
      if (!raw) return null;

      // "03 Aug 2026" / "3 August 2026"
      const textMatch = raw.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
      if (textMatch) {
        const day = Number.parseInt(textMatch[1], 10);
        const monthToken = textMatch[2].toLowerCase();
        const year = Number.parseInt(textMatch[3], 10);
        const monthIndex = this.monthNameToIndex(monthToken);
        if (monthIndex === null || !Number.isFinite(day) || !Number.isFinite(year)) return null;
        // Noon UTC avoids accidental day rollback when formatting.
        const parsed = new Date(Date.UTC(year, monthIndex, day, 12, 0, 0));
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      // "2026-08-03" or ISO datetime — use the calendar date part in UTC.
      const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDateMatch) {
        const year = Number.parseInt(isoDateMatch[1], 10);
        const month = Number.parseInt(isoDateMatch[2], 10) - 1;
        const day = Number.parseInt(isoDateMatch[3], 10);
        const parsed = new Date(Date.UTC(year, month, day, 12, 0, 0));
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      const fallback = new Date(raw);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    /**
     * @param {string} monthName
     * @returns {number | null}
     */
    monthNameToIndex(monthName) {
      const months = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
      ];
      const shortMonths = months.map((name) => name.slice(0, 3));
      const normalized = monthName.toLowerCase();
      const fullIndex = months.indexOf(normalized);
      if (fullIndex >= 0) return fullIndex;
      const shortIndex = shortMonths.indexOf(normalized.slice(0, 3));
      return shortIndex >= 0 ? shortIndex : null;
    }

    /** @param {unknown} value */
    formatLastUpdated(value) {
      if (value == null || value === '') return null;

      if (typeof value === 'string' && /^last\s*updated\b/i.test(value.trim())) {
        return value.trim();
      }

      const date = value instanceof Date ? value : new Date(String(value));
      if (Number.isNaN(date.getTime())) return null;

      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      }).formatToParts(date);

      /** @type {Record<string, string>} */
      const byType = {};
      for (const part of parts) {
        if (part.type !== 'literal') byType[part.type] = part.value;
      }

      const month = byType.month;
      const day = byType.day;
      const year = byType.year;
      const hour = byType.hour;
      const minute = byType.minute;
      const dayPeriod = byType.dayPeriod;

      if (!month || !day || !year || !hour || !minute || !dayPeriod) {
        const fallback = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/Chicago',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(date);
        return `Last Updated: ${fallback.replace(/\s+at\s+/i, ', ')} CST`;
      }

      return `Last Updated: ${month} ${day}, ${year}, ${hour}:${minute} ${dayPeriod} CST`;
    }

    /** @param {string} value */
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /** @param {unknown} value */
    toNumber(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const normalized = value.replace(/,/g, '').replace(/[^0-9.-]/g, '');
        if (!normalized) return null;
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    }

    /** @param {number} spotPrice */
    calculatePrices(spotPrice) {
      const karatPerTroyOz =
        goldPricing && typeof goldPricing.calculatePricesByKarat === 'function'
          ? goldPricing.calculatePricesByKarat(spotPrice)
          : {
              spot: spotPrice,
              '10k': spotPrice * 0.417,
              '14k': spotPrice * 0.585,
              '18k': spotPrice * 0.75,
              '22k': spotPrice * 0.9167,
              '24k': spotPrice,
            };

      /** @param {number | null | undefined} value */
      const toPerGram = (value) => {
        if (!Number.isFinite(value)) return null;
        if (goldPricing && typeof goldPricing.calculateGoldPricePerGram === 'function') {
          return goldPricing.calculateGoldPricePerGram(value);
        }
        return value / TROY_OUNCE_IN_GRAMS;
      };

      return {
        // Spot stays per troy ounce.
        spot: Number.isFinite(karatPerTroyOz.spot) ? karatPerTroyOz.spot : spotPrice,
        // Karat prices are displayed per gram.
        '10k': toPerGram(karatPerTroyOz['10k']),
        '14k': toPerGram(karatPerTroyOz['14k']),
        '18k': toPerGram(karatPerTroyOz['18k']),
        '22k': toPerGram(karatPerTroyOz['22k']),
        '24k': toPerGram(karatPerTroyOz['24k']),
      };
    }

    /** @param {string} key */
    getValueUnitSuffix(key) {
      return key === 'spot' ? '/oz' : '/gm';
    }

    /**
     * @param {number | null} value
     * @param {string} currencySymbol
     * @param {string} key
     */
    formatValue(value, currencySymbol, key) {
      const unitSuffix = this.getValueUnitSuffix(key);
      if (value === null || !Number.isFinite(value)) return `${currencySymbol}-- ${unitSuffix}`;
      return `${currencySymbol}${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)} ${unitSuffix}`;
    }

    renderUnavailable() {
      if (!this.config) return;
      if (this.config.livePriceEnabled) {
        this.setContainerVisible(false);
      } else {
        this.setContainerVisible(true);
      }
      const values = {
        spot: null,
        '10k': null,
        '14k': null,
        '18k': null,
        '22k': null,
        '24k': null,
      };
      this.render(values, null);
    }

    /**
     * @param {number} spotPrice
     * @param {string | null} [lastUpdatedText]
     */
    renderFromSpot(spotPrice, lastUpdatedText = this.lastUpdatedText) {
      this.render(this.calculatePrices(spotPrice), lastUpdatedText);
      this.setContainerVisible(true);
    }

    /**
     * @param {Record<string, number | null>} values
     * @param {string | null} [lastUpdatedText]
     */
    render(values, lastUpdatedText = null) {
      if (!this.group || !this.config) return;

      const itemsHtml = this.config.visibleKeys
        .map((key) => this.renderItemHtml(key, values[key] ?? null))
        .join('');
      const updatedHtml = this.renderLastUpdatedHtml(lastUpdatedText);
      const html = `${updatedHtml}${itemsHtml}`;
      const nextSignature = `${this.config.visibleKeys.join(',')}|${Object.values(values)
        .map((value) => (value === null ? '--' : value.toFixed(4)))
        .join('|')}|${this.config.currencySymbol}|${this.config.spotLabel}|${lastUpdatedText || ''}`;

      if (nextSignature === this.lastRenderedSignature) return;

      this.group.innerHTML = html;
      this.lastRenderedSignature = nextSignature;
    }

    /**
     * @param {string} key
     * @param {number | null} value
     */
    renderItemHtml(key, value) {
      if (!this.config) return '';

      const label = key === 'spot' ? `${this.config.spotLabel}:` : `${key.toUpperCase()}:`;
      const isSpot = key === 'spot';
      const valueClass = isSpot ? 'aone-gold-ticker__value aone-gold-ticker__value--spot' : 'aone-gold-ticker__value';
      const textValue = this.formatValue(value, this.config.currencySymbol, key);

      return `<span class="aone-gold-ticker__item" data-gold-key="${key}"><span class="aone-gold-ticker__label">${label}</span><span class="${valueClass}">${textValue}</span></span>`;
    }

    /** @param {string | null | undefined} text */
    renderLastUpdatedHtml(text) {
      if (!text) return '';
      const safeText = this.escapeHtml(text);
      return `<span class="aone-gold-ticker__item aone-gold-ticker__item--updated" data-gold-key="last-updated"><span class="aone-gold-ticker__label">${safeText}</span></span>`;
    }
  }

  /** @type {GoldTickerController} */
  const controller = runtimeWindow[CONTROLLER_KEY] || new GoldTickerController();
  runtimeWindow[CONTROLLER_KEY] = controller;

  /** @param {Document | HTMLElement | unknown} scope */
  function init(scope) {
    const root = scope instanceof Document || scope instanceof HTMLElement ? scope : document;
    const container = root.querySelector(CONTAINER_SELECTOR);
    if (!(container instanceof HTMLElement)) return;
    controller.mount(container);
  }

  /** @param {Event} event */
  function handleSectionUnload(event) {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    if (!target.querySelector(CONTAINER_SELECTOR)) return;
    controller.unmount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
